// Adapted from davinci-elections apps/web/src/theme/semantic.ts @ 6e23f58
import { defineSemanticTokens } from '@chakra-ui/react'
import { decoSemanticColors } from './deco'

/**
 * Semantic tokens allow us to define tokens, like colors, based on their usage
 * https://v2.chakra-ui.com/docs/styled-system/semantic-tokens
 */

// @deprecated: use the chakra built-in `bg` token directly
const chakra = {
  body: {
    bg: {
      value: '{colors.bg}',
    },
  },
}

// @deprecated: use the chakra built-in `fg`/`fg.muted` tokens directly
const texts = {
  primary: {
    value: '{colors.fg}',
  },
  subtle: {
    value: '{colors.fg.muted}',
  },
  dark: {
    value: '{colors.fg.muted}',
  },
}

export const colors = defineSemanticTokens.colors({
  chakra,
  // Overrides of the chakra built-in semantic scale — the canonical vocabulary
  // for surfaces and text (bg, bg.subtle, bg.muted, fg, fg.muted, border, …)
  // The layered surfaces (R113, contrast pass: the owner asked for more contrast between the menus and headers and the
  // body, which outranks the product rule "surfaces are never tinted"). From back to front:
  //   bg.canvas   the app background, behind everything
  //   bg.chrome   the frame: sidebar, top bar, bottom tab bar, registration header band and footer, with a
  //               `border.chrome` edge against the canvas (recessed below the canvas in light mode; in dark mode a
  //               clear step above the near-black page, at least 1.25:1, and below the cards)
  //   bg          a content surface: cards, panels, tables, form fields (raised: border and `shadows.surface`)
  //   bg.panel    a floating surface: menus, dialogs, popovers (`shadows.raised`)
  //   bg.subtle   an inset area inside a surface: table header row, a box nested in a card
  //   bg.field    a form field (white; darker than the card in dark mode)
  //   bg.muted    the hover fill of a row or a quiet control; bg.emphasized a stronger neutral fill
  // Text reaches 4.5:1 (fg, fg.muted, fg.subtle) and control outlines 3:1 (border.input) on every one of them, in both
  // modes (test/theme-contrast.test.ts).
  bg: {
    DEFAULT: {
      value: { _light: '{colors.white}', _dark: '{colors.neutral.900}' },
    },
    canvas: {
      value: { _light: '{colors.neutral.50}', _dark: '{colors.neutral.990}' },
    },
    chrome: {
      DEFAULT: { value: { _light: '{colors.neutral.100}', _dark: '{colors.neutral.925}' } },
      // Hover fill of a menu entry on the chrome.
      hover: { value: { _light: '{colors.neutral.150}', _dark: '{colors.neutral.875}' } },
    },
    panel: {
      value: { _light: '{colors.white}', _dark: '{colors.neutral.850}' },
    },
    subtle: {
      value: { _light: '{colors.neutral.25}', _dark: '{colors.neutral.950}' },
    },
    // A form field: white in light mode; in dark mode filled darker than the card it sits in, so an empty field still
    // reads as a place to type.
    field: {
      value: { _light: '{colors.white}', _dark: '{colors.neutral.950}' },
    },
    muted: {
      value: { _light: '{colors.neutral.250}', _dark: '{colors.neutral.800}' },
    },
    emphasized: {
      value: { _light: '{colors.neutral.200}', _dark: '{colors.neutral.700}' },
    },
  },
  // Error and success text on the page: Chakra's defaults (red.500, green.600) stay under 4.5:1 on white.
  // `fg.subtle`: Chakra's gray.400 is 2.6:1 on white; here it is the quietest text that still reads at 4.5:1.
  fg: {
    // Chakra's dark gray.400 is under 4.5:1 on the lighter dark panels and hover fills of the surface ladder.
    muted: { value: { _light: '{colors.gray.600}', _dark: '{colors.neutral.660}' } },
    subtle: { value: { _light: '{colors.neutral.600}', _dark: '{colors.neutral.640}' } },
    error: { value: { _light: '{colors.red.700}', _dark: '{colors.red.300}' } },
    success: { value: { _light: '{colors.green.700}', _dark: '{colors.green.300}' } },
    warning: { value: { _light: '{colors.orange.700}', _dark: '{colors.orange.300}' } },
  },
  // Decorative palette: icon tiles, step markers, illustration panels, stat tiles (see deco.ts).
  deco: decoSemanticColors,
  auth: {
    // The admin sign-in: a content card on the app background.
    bg: {
      value: '{colors.bg.canvas}',
    },
    card: {
      bg: {
        value: '{colors.bg}',
      },
      border: {
        value: '{colors.border}',
      },
    },
    // The showcase panel is dark in both color modes — it sits on a deep vertical accent
    // tone, so its foreground doesn't flip with the theme.
    showcase: {
      fg: {
        DEFAULT: {
          value: '{colors.white}',
        },
        muted: {
          value: '{colors.gray.400}',
        },
      },
      ring: {
        value: '{colors.separator}',
      },
      // Hairline between the quote and its attribution. Neutral white alpha rather than
      // `separator`, which is faintly blue and reads as a tint of its own on a coloured panel.
      rule: {
        value: 'rgba(255, 255, 255, 0.16)',
      },
    },
    // Tile the customer logos sit on, in the showcase panel and in the trust bar alike. Light in
    // both color modes on purpose: several logos are dark artwork on a transparent background and
    // disappear on a dark surface.
    logo: {
      tile: {
        bg: {
          value: '{colors.white}',
        },
        border: {
          value: {
            _light: '{colors.gray.200}',
            _dark: '{colors.separator}',
          },
        },
      },
    },
  },
  border: {
    // Structural edges: a card or a table against the canvas, a divider inside a surface.
    DEFAULT: {
      value: { _light: '{colors.neutral.200}', _dark: '{colors.neutral.750}' },
    },
    emphasized: {
      value: { _light: '{colors.neutral.300}', _dark: '{colors.neutral.700}' },
    },
    // The edge of the chrome (sidebar, top bar, tab bar) against the canvas.
    chrome: {
      value: { _light: '{colors.neutral.300}', _dark: '{colors.neutral.700}' },
    },
    // The outline of a form control (input, textarea, select, checkbox) and of a secondary (outline) button: at least
    // 3:1 against every surface it can sit on, in both modes (WCAG 1.4.11).
    input: {
      value: { _light: '{colors.neutral.500}', _dark: '{colors.neutral.550}' },
    },
    // The border and label colour of a disabled button: still at least 3:1 against every surface in both modes.
    control: {
      value: { _light: '{colors.neutral.500}', _dark: '{colors.neutral.550}' },
    },
    // @deprecated: use the chakra built-in `border` token directly
    dashboard: {
      value: '{colors.border}',
    },
    pagination: {
      active: {
        value: {
          _light: '{colors.gray.700}',
          _dark: '{colors.gray.500}',
        },
      },
    },
  },
  card: {
    pricing: {
      bg: {
        value: {
          _light: '{colors.white}',
          _dark: '{colors.ink.650}',
        },
      },
      border: {
        value: {
          _light: '{colors.gray.200}',
          _dark: '{colors.ink.700}',
        },
      },
      current: {
        bg: {
          value: {
            _light: '{colors.gray.100}',
            _dark: '{colors.ink.600}',
          },
        },
        // Accent tokens read the generated `brand.fg` / `brand.solid` /
        // `brand.contrast` slots rather than the raw 500 step: 500 is the exact
        // configured PRIMARY_COLOR, which carries no readability guarantee (a
        // pale brand renders this label at ~1.3:1 on white). With no
        // PRIMARY_COLOR these resolve to the same black/white as before.
        color: {
          value: {
            _light: '{colors.brand.fg}',
            _dark: '{colors.white}',
          },
        },
      },
      featured: {
        bg: {
          value: '{colors.gray.800}',
        },
        badge: {
          bg: {
            value: {
              _light: '{colors.brand.solid}',
              _dark: '{colors.white}',
            },
          },
          color: {
            value: {
              _light: '{colors.brand.contrast}',
              // The dark-mode badge is a *white* chip, so its label needs the
              // light-surface accent step (700), not the raw 500: a pale brand
              // at 500 reads ~1.3:1 on white. Still black without PRIMARY_COLOR.
              _dark: '{colors.brand.700}',
            },
          },
        },
        border: {
          value: {
            _light: '{colors.brand.solid}',
            _dark: '{colors.white}',
          },
        },
      },
    },
  },
  // @deprecated: use `bg.chrome`
  dashboard: {
    menu: {
      value: '{colors.bg.chrome}',
    },
  },
  // Placeholder text: an example, not a label, but still readable (4.5:1 on the field).
  input: {
    placeholder: {
      value: '{colors.fg.subtle}',
    },
  },
  // @deprecated: use the chakra built-in `border` token directly
  table: {
    border: {
      value: '{colors.border}',
    },
  },
  tabs: {
    tab: {
      color: {
        value: {
          _light: '{colors.gray.500}',
          _dark: '{colors.gray.400}',
        },
      },
      active: {
        // `brand.fg`, not the raw 500: the active tab label sits on white, and
        // an arbitrary PRIMARY_COLOR at 500 carries no contrast guarantee there.
        color: {
          value: {
            _light: '{colors.brand.fg}',
            _dark: '{colors.white}',
          },
        },
        bg: {
          value: '{colors.bg}',
        },
      },
    },
    bg: {
      value: '{colors.bg.muted}',
    },
  },
  texts,
})

/**
 * Elevation (R113): a content surface sits just above the canvas (`surface`), a menu, dialog or floating bar above
 * everything (`raised`). Layered, low opacity, tinted towards the surface ramp; in dark mode the borders carry the
 * structure and the shadow only deepens it.
 */
export const shadows = defineSemanticTokens.shadows({
  surface: {
    value: {
      _light: '0 1px 2px 0 rgba(22, 33, 27, 0.06), 0 1px 3px 0 rgba(22, 33, 27, 0.06)',
      _dark: '0 1px 2px 0 rgba(0, 0, 0, 0.5)',
    },
  },
  raised: {
    value: {
      _light: '0 10px 24px -6px rgba(22, 33, 27, 0.16), 0 2px 6px -2px rgba(22, 33, 27, 0.1)',
      _dark: '0 12px 28px -6px rgba(0, 0, 0, 0.7), 0 2px 6px -2px rgba(0, 0, 0, 0.5)',
    },
  },
  // The bottom tab bar and the floating action bar of a phone: a soft shadow cast upwards over the page.
  dockBottom: {
    value: {
      _light: '0 -4px 14px -8px rgba(22, 33, 27, 0.22)',
      _dark: '0 -4px 14px -6px rgba(0, 0, 0, 0.7)',
    },
  },
})

const semanticTokens = {
  colors,
  shadows,
}

export default semanticTokens
